/**
 * Voter page for a single poll — `/polls/{id}`.
 *
 * States the page can be in:
 *   1. Active poll, user hasn't voted yet → ballot UI with radios
 *      (single-select) or checkboxes (multi-select). Submit unlocks
 *      results.
 *   2. Active poll, user already voted → results bars with the user's
 *      selections highlighted. Shows "voted ✓" pill.
 *   3. Closed poll → final results, no ballot. Same highlight if the
 *      caller voted while it was open.
 *   4. Poll not found → 404 fallback.
 *
 * Vote rules echoed from the server (`allow_multiple`):
 *   - false: radio-style picker, one selection required.
 *   - true:  checkboxes, ≥1 selection required.
 */

import { api, ApiError } from '../api.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';

function _pollIdFromPath() {
  // /polls/{id}
  const parts = window.location.pathname.split('/').filter(Boolean);
  return parts[1] || null;
}

export async function renderPollVote(root) {
  const id = _pollIdFromPath();
  root.innerHTML = `
    <div class="appbar">
      <a href="javascript:void(0)" id="pv-back" class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Vote</div>
    </div>
    <div class="pad" id="pv-body">
      <div class="skel"></div>
      <div class="skel"></div>
    </div>
  `;
  document.getElementById('pv-back').addEventListener('click', () => {
    if (window.history.length > 1) window.history.back();
    else navigate('/');
  });
  if (!id) {
    document.getElementById('pv-body').innerHTML =
      `<div class="card center muted" style="padding:24px;">Poll not found.</div>`;
    return;
  }
  await loadAndPaint(id);
}

async function loadAndPaint(id) {
  const body = document.getElementById('pv-body');
  let poll;
  try {
    poll = await api(`/polls/${encodeURIComponent(id)}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) { navigate('/login'); return; }
    if (err instanceof ApiError && err.status === 404) {
      body.innerHTML = `
        <div class="card center muted" style="padding:24px;">
          <div style="font-family:var(--font-display);font-size:14px;color:#fff;margin-bottom:4px;">Poll not found</div>
          <div style="font-size:12px;">It may have been removed or never published.</div>
        </div>
      `;
      return;
    }
    body.innerHTML = `<div class="card" style="color:var(--pink-t)">${escape(err.message)}</div>`;
    return;
  }

  // selected state — start with whatever the server already has on
  // record for the caller (rehydrate the chosen options on refresh).
  const selected = new Set(poll.your_selections || []);
  body.innerHTML = renderBody(poll, selected);
  wireBody(poll, selected);
}

function renderBody(poll, selected) {
  const closesLine = poll.closes_at
    ? `Closes ${formatDate(poll.closes_at)}`
    : (poll.status === 'closed' ? `Closed ${formatDate(poll.closed_at)}` : 'No deadline');
  const subBits = [
    poll.allow_multiple ? 'Pick all that apply' : 'Pick one',
    closesLine,
  ];
  const hero = `
    <div class="hero violet center" style="padding:24px 18px;">
      <div style="font-family:var(--font-display);font-size:13px;letter-spacing:5px;opacity:0.92;font-weight:700;">
        POLL
      </div>
      <div style="font-family:var(--font-display);font-size:22px;color:#fff;font-weight:800;margin-top:8px;line-height:1.3;">
        ${escape(poll.question)}
      </div>
      ${poll.description
        ? `<div style="font-size:12.5px;color:rgba(255,255,255,0.85);font-weight:500;margin-top:6px;line-height:1.45;">${escape(poll.description)}</div>`
        : ''}
      <div class="muted" style="font-size:11px;font-weight:500;margin-top:8px;color:rgba(255,255,255,0.7);">
        ${escape(subBits.join(' · '))}
      </div>
      ${poll.has_voted
        ? `<span style="display:inline-block;margin-top:10px;padding:4px 12px;border-radius:999px;background:rgba(95,224,190,0.22);border:1px solid rgba(95,224,190,0.55);color:#5FE0BE;font-family:var(--font-display);font-size:11px;font-weight:700;letter-spacing:0.06em;">✓ VOTED</span>`
        : ''}
      ${poll.status === 'closed'
        ? `<span style="display:inline-block;margin-top:10px;padding:4px 12px;border-radius:999px;background:rgba(160,150,200,0.22);border:1px solid rgba(160,150,200,0.55);color:#A096C8;font-family:var(--font-display);font-size:11px;font-weight:700;letter-spacing:0.06em;">CLOSED</span>`
        : ''}
    </div>
  `;

  const showResults = poll.results !== null && poll.results !== undefined;
  const canVote = poll.status === 'active' && !poll.has_voted;
  return `
    ${hero}
    ${canVote ? renderBallot(poll, selected) : ''}
    ${showResults ? renderResults(poll, selected) : (!canVote ? `
      <div class="card muted center" style="padding:18px;">
        <div style="font-size:12.5px;">Vote to see the running tally.</div>
      </div>
    ` : '')}
    <a href="/" data-link class="btn ghost" style="margin-top:14px;">Back to home</a>
  `;
}

function renderBallot(poll, selected) {
  const multi = poll.allow_multiple;
  return `
    <div class="card" style="padding:14px;">
      <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:8px;">
        ${multi ? 'Pick all that apply' : 'Pick one option'}
      </div>
      <div id="pv-options">
        ${poll.options.map((o) => {
          const chosen = selected.has(o.id);
          return `
            <button type="button" class="opt pv-opt ${chosen ? 'violet-on' : ''}" data-option="${escape(o.id)}" style="text-align:left;">
              <span>${escape(o.label)}</span>
              <span aria-hidden="true">${chosen ? '✓' : '○'}</span>
            </button>
          `;
        }).join('')}
      </div>
      <button type="button" id="pv-submit" class="btn gold" style="margin-top:14px;" ${selected.size === 0 ? 'disabled' : ''}>
        Submit vote
      </button>
      <div class="muted" style="font-size:11px;text-align:center;margin-top:6px;">One ballot per poll — you can't undo this.</div>
    </div>
  `;
}

function renderResults(poll, selected) {
  const total = poll.total_ballots || 0;
  const max = Math.max(1, ...poll.results.map((o) => o.vote_count || 0));
  return `
    <h3 class="sec">Results · ${total} ballot${total === 1 ? '' : 's'}</h3>
    <div class="card" style="padding:14px;">
      ${poll.results.map((o) => {
        const mine = selected.has(o.id);
        const pct = Math.round(((o.vote_count || 0) / max) * 100);
        const sharePct = total > 0 ? Math.round(((o.vote_count || 0) / total) * 100) : 0;
        return `
          <div style="margin-bottom:12px;">
            <div class="row" style="font-size:13px;color:#fff;">
              <div class="grow" style="min-width:0;">
                ${escape(o.label)}
                ${mine ? '<span class="tag teal" style="margin-left:6px;font-size:9px;">YOUR PICK</span>' : ''}
              </div>
              <div style="font-family:var(--font-display);color:var(--gold-t);font-weight:700;font-size:13px;">${o.vote_count} · ${sharePct}%</div>
            </div>
            <div style="height:8px;background:#1a1432;border-radius:5px;margin-top:5px;overflow:hidden;">
              <div style="height:100%;width:${pct}%;background:${mine ? 'linear-gradient(90deg,#23C9A0,#5FE0BE)' : 'linear-gradient(90deg,#5B2C91,#E94BA2)'};"></div>
            </div>
          </div>
        `;
      }).join('')}
    </div>
  `;
}

function wireBody(poll, selected) {
  // Bind option taps. Single-select polls replace the selection; multi-
  // select polls toggle membership. Either way the Submit button enables
  // when ≥1 option is selected.
  document.querySelectorAll('.pv-opt').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.option;
      if (poll.allow_multiple) {
        if (selected.has(id)) selected.delete(id);
        else selected.add(id);
      } else {
        selected.clear();
        selected.add(id);
      }
      // Repaint only the ballot section so the visual stays snappy.
      document.querySelectorAll('.pv-opt').forEach((b) => {
        const isOn = selected.has(b.dataset.option);
        b.classList.toggle('violet-on', isOn);
        const mark = b.querySelector('[aria-hidden]');
        if (mark) mark.textContent = isOn ? '✓' : '○';
      });
      const submit = document.getElementById('pv-submit');
      if (submit) submit.disabled = selected.size === 0;
    });
  });

  const submit = document.getElementById('pv-submit');
  if (submit) {
    submit.addEventListener('click', async () => {
      if (selected.size === 0) return;
      submit.disabled = true;
      submit.textContent = 'Submitting…';
      try {
        await api(`/polls/${encodeURIComponent(poll.id)}/votes`, {
          method: 'POST',
          body: { option_ids: Array.from(selected) },
        });
        toast.success('Vote submitted.');
        // Re-fetch so the results unlock with the freshest counts —
        // simpler than splicing the response into the existing render.
        await loadAndPaint(poll.id);
      } catch (err) {
        submit.disabled = false;
        submit.textContent = 'Submit vote';
        if (err instanceof ApiError && err.code === 'already_voted') {
          // Hot-cache miss: a parallel tab already voted. Reload to
          // show the results state.
          await loadAndPaint(poll.id);
          return;
        }
        toast.error(err?.message || 'Could not submit vote.');
      }
    });
  }
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}
